/**
 * SubmitWarningsContent.tsx
 * Structured pre-commit warning body (2026-08-05) — replaces the wall-of-text
 * bullets in the Finalize / Commit Schedule confirm dialogs with scannable,
 * severity-grouped sections: a colored header per category and a compact table
 * (or chip list) underneath. Rendered by the shared ConfirmDialog via its
 * `body` slot at every commit door (Scenario Finalize, Quote/WO Commit
 * Schedule, Master Gantt bulk commit) — same data, same component, no drift.
 *
 * The plain-text `buildSubmitWarnings` sibling stays as the source of the
 * "does this check warrant a warn-dialog?" decision (null = clean) and the
 * category order; this component mirrors that order visually.
 *
 * Colors via theme tokens only (CODING-STANDARDS — no hex literals):
 *   hard (blocks nothing but WILL go live broken) — theme.schedule.conflictHard
 *   advisory (worth a look)                       — theme.schedule.maintenance
 *   soft (informational cross-plan contention)    — theme.schedule.conflictSoft
 */

import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import type { SubmitCheckResult } from '../../services/scheduleScenario';
import { weekOfMondayLabel } from './employeeScheduleShared';

type Severity = 'hard' | 'advisory' | 'soft';

function severityColor(theme: Theme, severity: Severity): string {
  if (severity === 'hard') return theme.schedule.conflictHard;
  if (severity === 'advisory') return theme.schedule.maintenance;
  return theme.schedule.conflictSoft;
}

const SEVERITY_ICON: Record<Severity, React.ElementType> = {
  hard: ErrorOutlineIcon,
  advisory: WarningAmberIcon,
  soft: InfoOutlinedIcon,
};

/** 'YYYY-MM-DD' → 'Aug 10' (UTC-safe, matches submitWarnings' fmtDay). */
function fmtDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** One category: colored icon + title header, content underneath. */
function Section({
  severity,
  title,
  hint,
  children,
}: {
  severity: Severity;
  title: string;
  hint?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const theme = useTheme();
  const color = severityColor(theme, severity);
  const Icon = SEVERITY_ICON[severity];
  return (
    <Box
      sx={{
        border: `1px solid ${alpha(color, 0.4)}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: alpha(color, 0.04),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75 }}>
        <Icon sx={{ fontSize: 16, color }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color }}>{title}</Typography>
        {hint && (
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{hint}</Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

const headCellSx = {
  fontSize: '0.64rem',
  fontWeight: 800,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  color: 'text.secondary',
  py: 0.4,
  lineHeight: 1.2,
};
const bodyCellSx = { fontSize: '0.74rem', py: 0.5, verticalAlign: 'top' };

/** Chip-style inline list (test names etc.). */
function ChipList({ items }: { items: string[] }): React.JSX.Element {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1.25, pb: 1 }}>
      {items.map((label) => (
        <Box
          key={label}
          component="span"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            px: 0.8,
            py: 0.2,
            borderRadius: '999px',
            border: `1px solid ${theme.border.color}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {label}
        </Box>
      ))}
    </Box>
  );
}

export interface SubmitWarningsContentProps {
  check: SubmitCheckResult;
}

/**
 * The structured warning sections for one SubmitCheckResult. Renders nothing
 * when the check is clean — callers normally gate on buildSubmitWarnings()
 * returning non-null before showing this at all.
 */
export const SubmitWarningsContent: React.FC<SubmitWarningsContentProps> = ({ check }) => {
  const theme = useTheme();
  if (check.clean) return <></>;
  const { unassignedTasks, overbookedWeeks, hardConflicts, crossScenarioHolds } = check;
  const unmappedTests = check.unmappedTests ?? [];
  const weekOverloaded = check.weekOverloaded ?? [];
  const plural = (n: number) => (n !== 1 ? 's' : '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
        This plan has issues that will go live as-is:
      </Typography>

      {/* G-4 — tests missing from the plan entirely (no catalog mapping). */}
      {unmappedTests.length > 0 && (
        <Section
          severity="advisory"
          title={`${unmappedTests.length} test${plural(unmappedTests.length)} NOT in this plan`}
          hint="no catalog mapping — generation skipped them"
        >
          <ChipList items={unmappedTests} />
        </Section>
      )}

      {/* Hard machine-day conflicts. */}
      {hardConflicts.length > 0 && (
        <Section
          severity="hard"
          title={`${hardConflicts.length} hard machine conflict${plural(hardConflicts.length)}`}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Machine</TableCell>
                <TableCell sx={headCellSx}>Days</TableCell>
                <TableCell sx={headCellSx}>Tests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hardConflicts.map((c, i) => (
                <TableRow key={`${c.machineCode}|${c.overlapStart}|${i}`}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{c.machineCode}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    {fmtDay(c.overlapStart)}
                    {c.overlapEnd !== c.overlapStart ? ` – ${fmtDay(c.overlapEnd)}` : ''}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{c.taskNames.join(', ') || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}

      {/* Wave 3 (Q3B) — assignments landing on a day off. */}
      {overbookedWeeks.length > 0 && (
        <Section
          severity="hard"
          title={`${overbookedWeeks.length} day-off conflict${plural(overbookedWeeks.length)}`}
          hint="assigned to work on a day they're away"
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Person</TableCell>
                <TableCell sx={headCellSx}>Days off with work assigned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overbookedWeeks.map((w) => (
                <TableRow key={`${w.userKey}|${w.weekStart}`}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{w.displayName}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    {(w.conflictDays ?? []).length > 0
                      ? (w.conflictDays ?? []).map(fmtDay).join(', ')
                      : `week of ${weekOfMondayLabel(w.weekStart)}`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}

      {/* Allocation-based overload advisory (2026-08-05). */}
      {weekOverloaded.length > 0 && (
        <Section
          severity="advisory"
          title={`${weekOverloaded.length} overloaded employee-week${plural(weekOverloaded.length)}`}
          hint="allocated over 100% across jobs"
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Person</TableCell>
                <TableCell sx={headCellSx}>Week</TableCell>
                <TableCell sx={headCellSx} align="right">Allocated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weekOverloaded.map((w) => (
                <TableRow key={`${w.userKey}|${w.weekStart}`}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{w.displayName}</TableCell>
                  <TableCell sx={bodyCellSx}>week of {weekOfMondayLabel(w.weekStart)}</TableCell>
                  <TableCell
                    sx={{ ...bodyCellSx, fontWeight: 800, color: theme.schedule.heatmapHigh }}
                    align="right"
                  >
                    {w.allocationPercent}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', px: 1.25, pb: 0.75 }}>
            Total allocation across all jobs that week — the same number as the Team capacity
            grid. Over 100% is allowed (advisory only).
          </Typography>
        </Section>
      )}

      {/* Tasks with no employee. */}
      {unassignedTasks.length > 0 && (
        <Section
          severity="advisory"
          title={`${unassignedTasks.length} test${plural(unassignedTasks.length)} with no employee assigned`}
        >
          <ChipList items={unassignedTasks.map((t) => t.taskName)} />
        </Section>
      )}

      {/* Cross-plan soft holds. */}
      {crossScenarioHolds.length > 0 && (
        <Section
          severity="soft"
          title={`${crossScenarioHolds.length} ${crossScenarioHolds.length === 1 ? 'person' : 'people'} also held in other draft plans`}
          hint="committing may force those plans to re-staff"
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Person</TableCell>
                <TableCell sx={headCellSx}>Also held in</TableCell>
                <TableCell sx={headCellSx} align="right">Days held</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {crossScenarioHolds.map((h) => (
                <TableRow key={h.userKey}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{h.displayName}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    {h.sources.length > 0
                      ? h.sources
                          .map((s) => [s.code, s.name].filter(Boolean).join(' '))
                          .filter(Boolean)
                          .join('; ') || 'another plan'
                      : 'another plan'}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx }} align="right">
                    {h.heldDays > 0 ? h.heldDays : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}
    </Box>
  );
};
